import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createNewArea,
  createNewArrow,
  createNewCircle,
  createNewIcon,
  createNewLegend,
  createNewLine,
  createNewPenStroke,
  createNewSquare,
  createNewText,
  deleteIcon,
  deletePenStroke,
  getAreas,
  getArrows,
  getCircles,
  getDrawingToolControlData,
  getIcons,
  getIsCreatingLegendItem,
  getLegends,
  getLines,
  getPenStrokes,
  getSelectedArea,
  getSelectedArrow,
  getSelectedCircle,
  getSelectedIconColor,
  getSelectedIconName,
  getSelectedIconSize,
  getSelectedLegend,
  getSelectedLine,
  getSelectedSquare,
  getSelectedText,
  getSelectedTool,
  getSquares,
  getTexts,
  loadDrawingToolControlData,
  setIsCreatingLegendItem,
  setPickedLegendColor,
  setSelectedArea,
  setSelectedArrow,
  setSelectedCircle,
  setSelectedIcon,
  setSelectedLegend,
  setSelectedLine,
  setSelectedSquare,
  setSelectedText,
  setSelectedTool,
  updateArea,
  updateArrow,
  updateCircle,
  updateIcon,
  updateLegend,
  updateLine,
  updateSquare,
  updateText,
} from '../../../store/lixaniPaint/slices/drawingToolSlice';
import { convertPdfToImage } from './utils/convertPdfToImage';
import { isWithinImage } from './utils/isWithinImage';

import uuidv4 from 'uuid/v4';

import { ToolCustomizer } from '../ToolSelector/ToolCustomizer/ToolCustomizer';
import { ToolSelector } from '../ToolSelector/ToolSelector';
import { getIconByName } from '../ToolSelector/iconOptions';

import { CircularProgress } from '@material-ui/core';
import { mdiHospitalBox } from '@mdi/js';
import Icon from '@mdi/react';
import Button from 'components/Button/Button';
import TextInput from 'components/TextInput/TextInput';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import Draggable from 'react-draggable';
import { useParams } from 'react-router-dom';
import { AREAPLAN_EDIT } from 'scenes/Permissions/constants/permissions';
import { selectIsRequiredPermission } from 'scenes/Permissions/store/selectors/personalPermissionsSelectors';
import { selectSelectedProject } from 'store/projects/selectors/projectsSelectors';
import { selectProject } from 'store/projects/thunks/selectProject.thunk';
import {
  getIsLoading,
  getSelectedAreaPlan,
  getVersionId,
  getVersionName,
  selectAreaPlan,
  setVersionId,
  setVersionName,
} from '../store/slices/areaPlanningSlice';
import {
  getProjectAreaplansThunk,
  saveAreaplanThunk,
  saveNewAreaplanVersionThunk,
  updateAreaPlanThunk,
} from '../store/thunks/areaPlanThunks';
import { downloadCanvasAsPdf } from './utils/downloadAsPdf';
import {
  drawAreaPreview,
  drawArrow,
  drawLinePreview,
} from './utils/drawingUtils/drawingUtils';
import {
  handleAreaClick,
  handleArrowClick,
  handleCircleClick,
  handleLegendClick,
  handleSquareClick,
  handleTextClick,
} from './utils/handlers/clickHandlers';
import {
  handleAreaSelection,
  handleArrowSelection,
  handleLegendSelection,
  handleLineSelection,
  handleSquareSelection,
  handleTextSelection,
} from './utils/handlers/mouseDownHandlers';
import {
  handleAreaDrag,
  handleArrowDrag,
  handleLegendDrag,
  handleLineDrag,
  handleSquareDrag,
  handleTextDrag,
} from './utils/handlers/mouseMoveHandlers';
import { findClickedElement } from './utils/legendColorPicking';
import {
  renderArea,
  renderArrow,
  renderCurrentStroke,
  renderLegend,
  renderLine,
  renderPenStroke,
  renderSquare,
  renderText,
} from './utils/renderingUtils';

export const ImageCanvas = () => {
  const dispatch = useDispatch();
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lineStart, setLineStart] = useState(null);
  const [linePoints, setLinePoints] = useState([]);
  const [arrowStart, setArrowStart] = useState(null);
  const [areaPoints, setAreaPoints] = useState([]);
  const [currentStroke, setCurrentStroke] = useState([]);
  const [file, setFile] = useState(null);
  const [planName, setPlanName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragPoint, setDragPoint] = useState(null);
  const [dragPointIndex, setDragPointIndex] = useState(null);
  const [isBackgroundLoaded, setIsBackgroundLoaded] = useState(false);
  const [squareStart, setSquareStart] = useState(null);
  const [circleStart, setCircleStart] = useState(null);
  const [legendStart, setLegendStart] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [draggedCircle, setDraggedCircle] = useState(null);

  const canvasRef = useRef(null);
  const backgroundImageRef = useRef(null);

  const controlData = useSelector(getDrawingToolControlData);
  const selectedTool = useSelector(getSelectedTool);

  const createdAreas = useSelector(getAreas);
  const createdTexts = useSelector(getTexts);
  const createdLines = useSelector(getLines);
  const createdArrows = useSelector(getArrows);
  const createdPenStrokes = useSelector(getPenStrokes);
  const createdSquares = useSelector(getSquares);
  const createdCircles = useSelector(getCircles);
  const createdLegends = useSelector(getLegends);
  const createdIcons = useSelector(getIcons);
  const selectedLine = useSelector(getSelectedLine);
  const selectedArea = useSelector(getSelectedArea);
  const selectedArrow = useSelector(getSelectedArrow);
  const selectedText = useSelector(getSelectedText);
  const selectedSquare = useSelector(getSelectedSquare);
  const selectedCircle = useSelector(getSelectedCircle);
  const selectedLegend = useSelector(getSelectedLegend);
  const selectedIconName = useSelector(getSelectedIconName);
  const selectedIconSize = useSelector(getSelectedIconSize);
  const selectedIconColor = useSelector(getSelectedIconColor);
  const isCreatingLegendItem = useSelector(getIsCreatingLegendItem);

  const project = useSelector(selectSelectedProject);

  const isPermissionToModifyAreaplans = useSelector((state) =>
    selectIsRequiredPermission(state, AREAPLAN_EDIT)
  );

  const cornerCircleDiameter = 6;

  const selectedPlan = useSelector(getSelectedAreaPlan);
  const versionId = useSelector(getVersionId);
  const versionName = useSelector(getVersionName);
  const isLoading = useSelector(getIsLoading);

  let { projectId, id } = useParams();

  useEffect(() => {
    if (project?.id) {
      projectId = project.id;
    } else if (projectId) {
      dispatch(selectProject(projectId));
    }

    if (versionName) {
      setPlanName(versionName);
      dispatch(setVersionName(null));
    }

    dispatch(getProjectAreaplansThunk({ projectId })).then(() => {
      dispatch(selectAreaPlan(id));
    });
  }, [projectId]);

  const handleLegendColorPicking = (x, y) => {
    const elements = {
      areas: createdAreas,
      lines: createdLines,
      arrows: createdArrows,
      squares: createdSquares,
      circles: createdCircles,
      texts: createdTexts,
    };

    const result = findClickedElement(x, y, elements);
    if (result) {
      dispatch(
        setPickedLegendColor({
          color: result.color,
          text: result.text,
          tool: result.tool,
        })
      );
      dispatch(setIsCreatingLegendItem(false));
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (selectedPlan) {
      dispatch(loadDrawingToolControlData(selectedPlan));
      setBackgroundImage(selectedPlan.mediaUrl);
      setFile(selectedPlan.mediaUrl);
      setPlanName(selectedPlan.name);
    }
  }, [selectedPlan]);

  const clearAllSelections = () => {
    dispatch(setSelectedArea(null));
    dispatch(setSelectedText(null));
    dispatch(setSelectedLine(null));
    dispatch(setSelectedArrow(null));
    dispatch(setSelectedSquare(null));
    dispatch(setSelectedLegend(null));
    dispatch(setSelectedCircle(null));
  };

  useEffect(() => {
    setSquareStart(null);
    setCircleStart(null);
    setLegendStart(null);
    setLineStart(null);
    setArrowStart(null);
    setAreaPoints([]);
    setLinePoints([]);
  }, [selectedTool]);

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    try {
      if (selectedFile.type === 'application/pdf') {
        const images = await convertPdfToImage(selectedFile);
        const imageBlob = images[0];
        setFile(imageBlob);
        const imageUrl = URL.createObjectURL(imageBlob);
        setBackgroundImage(imageUrl);
      } else if (selectedFile.type.startsWith('image/')) {
        const imageBlob = new Blob([selectedFile], { type: selectedFile.type });
        setFile(imageBlob);
        const imageUrl = URL.createObjectURL(imageBlob);
        setBackgroundImage(imageUrl);
      }
    } catch (error) {
      console.error('Error processing file:', error);
    }
  };

  const handleSaveAreaplan = () => {
    if (!file) {
      console.error('No file selected');
      enqueueSnackbar('Tiedostoa ei ole valittu!', {
        variant: 'error',
      });
      return;
    }

    if (!planName) {
      console.error('No name selected');
      enqueueSnackbar('Anna suunnitelmalle nimi!', {
        variant: 'error',
      });
      return;
    }

    if (!id) id = uuidv4();

    const areaplanDraft = {
      id,
      projectId,
      versionId: versionId || selectedPlan?.versionId || uuidv4(),
      createdAreas,
      createdTexts,
      createdLines,
      createdArrows,
      createdPenStrokes,
      createdSquares,
      createdCircles,
      createdLegends,
      createdIcons,
      createdAt: selectedPlan?.createdAt || moment(),
      lastUpdatedAt: moment(),
      name: planName,
    };

    dispatch(setVersionId(null));

    if (!selectedPlan) {
      dispatch(
        saveAreaplanThunk({
          areaplanDraft,
          fileId: id,
          fileBinary: file,
        })
      ).then(() => {
        enqueueSnackbar('Suunnitelma tallennettu onnistuneesti!', {
          variant: 'success',
        });
      });
    } else {
      dispatch(updateAreaPlanThunk({ areaplan: areaplanDraft })).then(() => {
        enqueueSnackbar('Suunnitelma päivitetty onnistuneesti!', {
          variant: 'success',
        });
      });
    }
  };

  const handleCreateNewVersion = () => {
    if (!file) {
      console.error('No file selected');
      enqueueSnackbar('Tiedostoa ei ole valittu!', {
        variant: 'error',
      });
      return;
    }

    if (!planName) {
      console.error('No name selected');
      enqueueSnackbar('Anna suunnitelmalle nimi!', {
        variant: 'error',
      });
      return;
    }

    const urlParts = selectedPlan.mediaUrl.split('/');
    const key = urlParts.slice(3).join('/');

    const areaplanDraft = {
      id: uuidv4(),
      projectId,
      versionId: selectedPlan.versionId,
      key: key,
      createdAreas,
      createdTexts,
      createdLines,
      createdArrows,
      createdPenStrokes,
      createdSquares,
      createdCircles,
      createdLegends,
      createdIcons,
      createdAt: moment(),
      name: planName,
    };

    dispatch(saveNewAreaplanVersionThunk({ areaplanDraft })).then(() => {
      enqueueSnackbar('Suunnitelma tallennettu onnistuneesti!', {
        variant: 'success',
      });
    });
  };

  useEffect(() => {
    if (backgroundImage && canvasRef.current) {
      setIsBackgroundLoaded(false);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.crossOrigin = 'anonymous';
      img.onload = () => {
        canvas.width = 1200;
        canvas.height = 800;

        const canvasAspect = canvas.width / canvas.height;
        const imageAspect = img.width / img.height;

        let drawWidth, drawHeight, drawX, drawY;

        if (imageAspect > canvasAspect) {
          drawWidth = canvas.width;
          drawHeight = canvas.width / imageAspect;
          drawX = 0;
          drawY = (canvas.height - drawHeight) / 2;
        } else {
          drawWidth = canvas.height * imageAspect;
          drawHeight = canvas.height;
          drawX = (canvas.width - drawWidth) / 2;
          drawY = 0;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

        backgroundImageRef.current = {
          img,
          drawX,
          drawY,
          drawWidth,
          drawHeight,
        };
        setIsBackgroundLoaded(true);
      };

      img.onerror = (error) => {
        setIsBackgroundLoaded(false);
      };

      img.src = backgroundImage;
    }
  }, [backgroundImage]);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (backgroundImageRef.current) {
      const { img, drawX, drawY, drawWidth, drawHeight } =
        backgroundImageRef.current;
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    }

    if (!isBackgroundLoaded) return;

    createdAreas.forEach((area) => {
      renderArea(ctx, area, cornerCircleDiameter);
    });

    createdTexts.forEach((text) => {
      renderText(ctx, text);
    });

    createdLines.forEach((line) => {
      renderLine(ctx, line, cornerCircleDiameter);
    });

    createdArrows.forEach((arrow) => {
      renderArrow(ctx, arrow, cornerCircleDiameter);
    });

    createdPenStrokes.forEach((stroke) => {
      renderPenStroke(ctx, stroke);
    });

    createdSquares.forEach((square) => {
      renderSquare(ctx, square, cornerCircleDiameter);
    });

    createdCircles.forEach((circle) => {
      // Use dragged circle position if currently dragging this circle
      const renderCircle =
        isDragging && draggedCircle && draggedCircle.id === circle.id
          ? draggedCircle
          : circle;

      ctx.beginPath();
      ctx.ellipse(
        renderCircle.x,
        renderCircle.y,
        renderCircle.radiusX,
        renderCircle.radiusY,
        0,
        0,
        2 * Math.PI
      );
      ctx.strokeStyle = renderCircle.lineColor || '#000000';
      ctx.lineWidth = renderCircle.lineWidth || 2;
      ctx.stroke();
      if (renderCircle.fillColor) {
        ctx.fillStyle = renderCircle.fillColor;
        ctx.fill();
      }

      // Draw drag handles if selected
      if (selectedCircle && selectedCircle.id === renderCircle.id) {
        const handleRadius = cornerCircleDiameter / 2;
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;

        const handles = [
          {
            x: renderCircle.x - renderCircle.radiusX,
            y: renderCircle.y - renderCircle.radiusY,
          },
          {
            x: renderCircle.x + renderCircle.radiusX,
            y: renderCircle.y - renderCircle.radiusY,
          },
          {
            x: renderCircle.x - renderCircle.radiusX,
            y: renderCircle.y + renderCircle.radiusY,
          },
          {
            x: renderCircle.x + renderCircle.radiusX,
            y: renderCircle.y + renderCircle.radiusY,
          },
        ];

        handles.forEach((handle) => {
          ctx.beginPath();
          ctx.arc(handle.x, handle.y, handleRadius, 0, 2 * Math.PI);
          ctx.fill();
          ctx.stroke();
        });
      }
    });

    renderCurrentStroke(ctx, currentStroke, controlData);

    if (squareStart && selectedTool === 'square') {
      ctx.beginPath();
      ctx.setLineDash([5, 5]);
      ctx.rect(
        squareStart.x,
        squareStart.y,
        mousePos.x - squareStart.x,
        mousePos.y - squareStart.y
      );
      ctx.strokeStyle = controlData.lineColor;
      ctx.lineWidth = controlData.lineWidth;
      ctx.stroke();
      ctx.setLineDash([]);
    }

    if (circleStart && selectedTool === 'circle') {
      ctx.beginPath();
      ctx.setLineDash([5, 5]);
      const radiusX = Math.abs(mousePos.x - circleStart.x);
      const radiusY = Math.abs(mousePos.y - circleStart.y);
      ctx.ellipse(
        circleStart.x,
        circleStart.y,
        radiusX,
        radiusY,
        0,
        0,
        2 * Math.PI
      );
      ctx.strokeStyle = controlData.lineColor;
      ctx.lineWidth = controlData.lineWidth;
      ctx.stroke();
      ctx.setLineDash([]);
    }

    if (linePoints.length > 0 && selectedTool === 'line') {
      drawLinePreview(ctx, linePoints, mousePos, controlData);
    }

    if (arrowStart && selectedTool === 'arrow') {
      ctx.setLineDash([5, 5]);
      drawArrow(
        ctx,
        arrowStart.x,
        arrowStart.y,
        mousePos.x,
        mousePos.y,
        controlData.lineWidth,
        controlData.lineColor
      );
      ctx.setLineDash([]);
    }

    if (areaPoints.length > 0 && selectedTool === 'area') {
      drawAreaPreview(ctx, areaPoints, mousePos, controlData);
    }

    createdLegends.forEach((legend) => {
      renderLegend(ctx, legend, cornerCircleDiameter);
    });

    if (legendStart && selectedTool === 'legend') {
      ctx.beginPath();
      ctx.setLineDash([5, 5]);
      ctx.rect(
        legendStart.x,
        legendStart.y,
        mousePos.x - legendStart.x,
        mousePos.y - legendStart.y
      );
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [
    createdAreas,
    createdTexts,
    createdLines,
    createdArrows,
    createdPenStrokes,
    createdSquares,
    createdCircles,
    createdLegends,
    createdIcons,
    currentStroke,
    controlData,
    isBackgroundLoaded,
    squareStart,
    circleStart,
    legendStart,
    lineStart,
    arrowStart,
    areaPoints,
    linePoints,
    mousePos,
    draggedCircle,
    selectedTool,
  ]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);

    if (!isWithinImage(x, y, backgroundImageRef)) return;

    setIsDrawing(true);
    setCurrentStroke([{ x, y }]);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);

    if (!isWithinImage(x, y, backgroundImageRef)) {
      setIsDrawing(false);
      return;
    }

    if (selectedTool === 'eraser') {
      const eraserRadius = controlData.lineWidth || 20;
      createdPenStrokes.forEach((stroke) => {
        const intersects = stroke.points.some((point) => {
          const distance = Math.sqrt((x - point.x) ** 2 + (y - point.y) ** 2);
          return distance <= eraserRadius;
        });
        if (intersects) {
          dispatch(deletePenStroke({ id: stroke.id }));
        }
      });
    } else {
      setCurrentStroke((prev) => [...prev, { x, y }]);
    }
  };

  const stopDrawing = () => {
    if (isDrawing && currentStroke.length > 1) {
      const newStroke = {
        id: uuidv4(),
        points: currentStroke,
        lineWidth: controlData.lineWidth,
        lineCap: controlData.lineCap,
        lineColor: controlData.lineColor,
      };
      dispatch(createNewPenStroke(newStroke));
    }
    setIsDrawing(false);
    setCurrentStroke([]);
  };

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);

    if (!isWithinImage(x, y, backgroundImageRef)) return;

    if (selectedTool === 'line') {
      setLinePoints([...linePoints, { x, y }]);
      setMousePos({ x, y });
    } else if (selectedTool === 'arrow') {
      handleArrowClick(
        x,
        y,
        ctx,
        arrowStart,
        controlData,
        setArrowStart,
        setMousePos,
        dispatch,
        createNewArrow,
        setSelectedArrow
      );
    } else if (selectedTool === 'area') {
      if (
        handleAreaClick(
          x,
          y,
          ctx,
          areaPoints,
          controlData,
          setAreaPoints,
          setMousePos,
          dispatch,
          createNewArea,
          setSelectedArea
        )
      )
        return;
    } else if (selectedTool === 'text') {
      handleTextClick(
        x,
        y,
        ctx,
        controlData,
        dispatch,
        createNewText,
        setSelectedText
      );
    } else if (selectedTool === 'square') {
      handleSquareClick(
        x,
        y,
        ctx,
        squareStart,
        controlData,
        setSquareStart,
        setMousePos,
        dispatch,
        createNewSquare,
        setSelectedSquare
      );
    } else if (selectedTool === 'circle') {
      handleCircleClick(
        x,
        y,
        ctx,
        circleStart,
        controlData,
        setCircleStart,
        setMousePos,
        dispatch,
        createNewCircle,
        setSelectedCircle
      );
    } else if (selectedTool === 'legend') {
      handleLegendClick(
        x,
        y,
        ctx,
        legendStart,
        controlData,
        setLegendStart,
        setMousePos,
        dispatch,
        createNewLegend,
        setSelectedLegend
      );
    } else if (selectedTool === 'icon') {
      const iconData = getIconByName(selectedIconName);
      const newIcon = {
        x,
        y: y - canvas.height,
        iconType: iconData.name,
        iconPath: iconData.icon,
        size: selectedIconSize,
        color: selectedIconColor,
        id: uuidv4(),
      };
      dispatch(createNewIcon(newIcon));
      dispatch(setSelectedIcon(newIcon));
    }
  };

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);

    if (
      (squareStart && selectedTool === 'square') ||
      (circleStart && selectedTool === 'circle') ||
      (legendStart && selectedTool === 'legend') ||
      (linePoints.length > 0 && selectedTool === 'line') ||
      (arrowStart && selectedTool === 'arrow') ||
      (areaPoints.length > 0 && selectedTool === 'area')
    ) {
      setMousePos({ x, y });
    }

    if (isDragging && selectedLine && (dragPoint || dragPointIndex !== null)) {
      handleLineDrag(
        x,
        y,
        selectedLine,
        dragPoint,
        dragPointIndex,
        dispatch,
        updateLine,
        setSelectedLine
      );
      return;
    }

    if (isDragging && selectedArea && dragPointIndex !== null) {
      handleAreaDrag(
        x,
        y,
        selectedArea,
        dragPointIndex,
        dispatch,
        updateArea,
        setSelectedArea
      );
      return;
    }

    if (isDragging && selectedArrow && dragPoint) {
      handleArrowDrag(
        x,
        y,
        selectedArrow,
        dragPoint,
        dispatch,
        updateArrow,
        setSelectedArrow
      );
      return;
    }

    if (isDragging && selectedText) {
      handleTextDrag(x, y, selectedText, dispatch, updateText, setSelectedText);
      return;
    }

    if (isDragging && selectedSquare && dragPoint) {
      handleSquareDrag(
        x,
        y,
        selectedSquare,
        dragPoint,
        dispatch,
        updateSquare,
        setSelectedSquare
      );
      return;
    }

    if (isDragging && selectedCircle && dragPoint) {
      let updatedCircle = { ...selectedCircle };

      if (dragPoint === 'body') {
        updatedCircle.x = x;
        updatedCircle.y = y;
      } else {
        // Calculate new radius based on distance from center
        const newRadiusX = Math.abs(x - selectedCircle.x);
        const newRadiusY = Math.abs(y - selectedCircle.y);

        // Use the maximum of the two radii to maintain circular shape
        const newRadius = Math.max(newRadiusX, newRadiusY);
        updatedCircle.radiusX = newRadius;
        updatedCircle.radiusY = newRadius;
      }

      setDraggedCircle(updatedCircle);
      return;
    }

    if (isDragging && selectedLegend && dragPoint) {
      handleLegendDrag(
        x,
        y,
        selectedLegend,
        dragPoint,
        dispatch,
        updateLegend,
        setSelectedLegend
      );
      return;
    }

    if (selectedTool === 'pen' || selectedTool === 'eraser') {
      draw(e);
    }
  };

  const handleMouseUp = () => {
    // Update Redux with final dragged position
    if (isDragging && draggedCircle) {
      dispatch(updateCircle(draggedCircle));
      dispatch(setSelectedCircle(draggedCircle));
      setDraggedCircle(null);
    }

    setIsDragging(false);
    setDragPoint(null);
    setDragPointIndex(null);
    if (selectedTool === 'pen' || selectedTool === 'eraser') {
      stopDrawing();
    }
  };

  const handleRightClick = (e) => {
    e.preventDefault();
    if (selectedTool === 'line' && linePoints.length > 0) {
      if (linePoints.length >= 2) {
        const newLineItem = {
          points: linePoints,
          lineWidth: controlData.lineWidth,
          lineColor: controlData.lineColor,
          id: uuidv4(),
        };
        dispatch(createNewLine(newLineItem));
        dispatch(setSelectedLine(newLineItem));
      }
      setLinePoints([]);
      return false;
    } else if (selectedTool === 'area' && areaPoints.length > 0) {
      setAreaPoints([]);
      return false;
    } else if (selectedTool === 'arrow' && arrowStart) {
      setArrowStart(null);
      return false;
    } else if (selectedTool === 'square' && squareStart) {
      setSquareStart(null);
      return false;
    } else if (selectedTool === 'circle' && circleStart) {
      setCircleStart(null);
      return false;
    } else if (selectedTool === 'legend' && legendStart) {
      setLegendStart(null);
      return false;
    }
  };

  const handleMouseDown = (e) => {
    if (e.button === 2) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);

    if (
      (selectedTool === 'line' && linePoints.length > 0) ||
      (selectedTool === 'arrow' && arrowStart) ||
      (selectedTool === 'area' && areaPoints.length > 0) ||
      (selectedTool === 'square' && squareStart) ||
      (selectedTool === 'circle' && circleStart) ||
      (selectedTool === 'legend' && legendStart)
    ) {
      handleCanvasClick(e);
      return;
    }

    if (isCreatingLegendItem) {
      if (handleLegendColorPicking(x, y)) return;
    }

    const tolerance = 8;

    if (
      handleLineSelection(
        x,
        y,
        createdLines,
        tolerance,
        setIsDragging,
        setDragPointIndex,
        setDragPoint,
        clearAllSelections,
        dispatch,
        setSelectedTool,
        setSelectedLine
      )
    )
      return;

    if (
      handleArrowSelection(
        x,
        y,
        createdArrows,
        tolerance,
        setIsDragging,
        setDragPoint,
        clearAllSelections,
        dispatch,
        setSelectedTool,
        setSelectedArrow
      )
    )
      return;

    if (
      handleTextSelection(
        x,
        y,
        createdTexts,
        canvasRef,
        setIsDragging,
        clearAllSelections,
        dispatch,
        setSelectedTool,
        setSelectedText
      )
    )
      return;

    if (
      handleAreaSelection(
        x,
        y,
        createdAreas,
        tolerance,
        setIsDragging,
        setDragPointIndex,
        clearAllSelections,
        dispatch,
        setSelectedTool,
        setSelectedArea
      )
    )
      return;

    if (
      handleSquareSelection(
        x,
        y,
        createdSquares,
        tolerance,
        setIsDragging,
        setDragPoint,
        clearAllSelections,
        dispatch,
        setSelectedTool,
        setSelectedSquare
      )
    )
      return;

    // Check if clicking on handles of already selected circle first
    if (selectedCircle) {
      const handles = [
        {
          x: selectedCircle.x - selectedCircle.radiusX,
          y: selectedCircle.y - selectedCircle.radiusY,
          type: 'tl',
        },
        {
          x: selectedCircle.x + selectedCircle.radiusX,
          y: selectedCircle.y - selectedCircle.radiusY,
          type: 'tr',
        },
        {
          x: selectedCircle.x - selectedCircle.radiusX,
          y: selectedCircle.y + selectedCircle.radiusY,
          type: 'bl',
        },
        {
          x: selectedCircle.x + selectedCircle.radiusX,
          y: selectedCircle.y + selectedCircle.radiusY,
          type: 'br',
        },
      ];

      const clickedHandle = handles.find((handle) => {
        const handleDistance = Math.sqrt(
          (x - handle.x) ** 2 + (y - handle.y) ** 2
        );
        return handleDistance <= 15;
      });

      if (clickedHandle) {
        setIsDragging(true);
        setDragPoint(clickedHandle.type);
        return;
      }

      // Check if clicking on selected circle body
      const distance = Math.sqrt(
        (x - selectedCircle.x) ** 2 + (y - selectedCircle.y) ** 2
      );
      if (
        distance <=
        Math.max(selectedCircle.radiusX, selectedCircle.radiusY) + tolerance
      ) {
        setIsDragging(true);
        setDragPoint('body');
        return;
      }
    }

    // Simple circle selection logic
    const clickedCircle = createdCircles.find((circle) => {
      const distance = Math.sqrt((x - circle.x) ** 2 + (y - circle.y) ** 2);
      return distance <= Math.max(circle.radiusX, circle.radiusY) + tolerance;
    });

    if (clickedCircle) {
      clearAllSelections();
      dispatch(setSelectedCircle(clickedCircle));

      // Check if clicking on a drag handle
      const handles = [
        {
          x: clickedCircle.x - clickedCircle.radiusX,
          y: clickedCircle.y - clickedCircle.radiusY,
          type: 'tl',
        },
        {
          x: clickedCircle.x + clickedCircle.radiusX,
          y: clickedCircle.y - clickedCircle.radiusY,
          type: 'tr',
        },
        {
          x: clickedCircle.x - clickedCircle.radiusX,
          y: clickedCircle.y + clickedCircle.radiusY,
          type: 'bl',
        },
        {
          x: clickedCircle.x + clickedCircle.radiusX,
          y: clickedCircle.y + clickedCircle.radiusY,
          type: 'br',
        },
      ];

      const clickedHandle = handles.find((handle) => {
        const handleDistance = Math.sqrt(
          (x - handle.x) ** 2 + (y - handle.y) ** 2
        );
        return handleDistance <= 15;
      });

      if (clickedHandle) {
        setIsDragging(true);
        setDragPoint(clickedHandle.type);
      } else {
        setIsDragging(true);
        setDragPoint('body');
      }
      return;
    }

    if (
      handleLegendSelection(
        x,
        y,
        createdLegends,
        tolerance,
        setIsDragging,
        setDragPoint,
        clearAllSelections,
        dispatch,
        setSelectedTool,
        setSelectedLegend
      )
    )
      return;

    if (selectedTool === 'pen' || selectedTool === 'eraser') {
      startDrawing(e);
    } else if (
      selectedTool === 'line' ||
      selectedTool === 'arrow' ||
      selectedTool === 'area' ||
      selectedTool === 'text' ||
      selectedTool === 'square' ||
      selectedTool === 'circle' ||
      selectedTool === 'legend' ||
      selectedTool === 'icon' ||
      selectedTool === 'select'
    ) {
      handleCanvasClick(e);
    }
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}
    >
      {isLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <CircularProgress size={60} style={{ color: '#1687af' }} />
          <div style={{ color: 'white', fontSize: '18px', fontWeight: '500' }}>
            {selectedPlan
              ? 'Päivitetään suunnitelmaa...'
              : 'Tallennetaan suunnitelmaa...'}
          </div>
        </div>
      )}
      <div
        style={{
          backgroundColor: '#1687af',
          borderBottom: '1px solid #d1d1d1',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        {isPermissionToModifyAreaplans ? (
          <label
            style={{
              backgroundColor: '#0078d4',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <input
              type="file"
              accept="application/pdf, image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            Valitse Kuva/PDF
          </label>
        ) : null}
        <TextInput
          type="text"
          placeholder="Suunnitelman nimi"
          disabled={!isPermissionToModifyAreaplans}
          value={planName}
          onChange={(e) => setPlanName(e.target.value)}
        />

        {isPermissionToModifyAreaplans ? <ToolSelector /> : null}

        {isPermissionToModifyAreaplans ? (
          <Button elevated onClick={handleSaveAreaplan} disabled={isLoading}>
            Tallenna
          </Button>
        ) : null}

        {isPermissionToModifyAreaplans ? (
          <Button
            elevated
            disabled={!selectedPlan || isLoading}
            onClick={handleCreateNewVersion}
          >
            Luo uusi versio
          </Button>
        ) : null}

        <Button
          elevated
          disabled={!selectedPlan || isLoading}
          onClick={async () => await downloadCanvasAsPdf(canvasRef, planName)}
        >
          Lataa PDF
        </Button>
      </div>
      <div
        style={{ display: 'flex', width: '100%', height: 'calc(100vh - 80px)' }}
      >
        {isPermissionToModifyAreaplans ? (
          <div
            style={{
              width: '300px',
              padding: '16px',
              borderRight: '1px solid #d1d1d1',
              backgroundColor: '#1687af',
              fontFamily: 'Segoe UI, sans-serif',
            }}
          >
            <ToolCustomizer />
          </div>
        ) : null}

        <div
          style={{
            flex: 1,
            padding: '10px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <canvas
              ref={canvasRef}
              style={{
                cursor: 'crosshair',
                width: '100%',
                height: '100%',
                maxWidth: '90vw',
                maxHeight: '70vh',
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onContextMenu={handleRightClick}
            ></canvas>
            {createdIcons.map((icon) => {
              const canvas = canvasRef.current;
              if (!canvas) return null;
              const rect = canvas.getBoundingClientRect();
              const scaleX = rect.width / canvas.width;
              const scaleY = rect.height / canvas.height;
              const iconPath = icon.iconPath || mdiHospitalBox;

              return (
                <Draggable
                  key={icon.id}
                  position={{
                    x: icon.x * scaleX - 12,
                    y: icon.y * scaleY - 12,
                  }}
                  onDrag={(e, data) => {
                    const newX = (data.x + 12) / scaleX;
                    const newY = (data.y + 12) / scaleY;

                    dispatch(updateIcon({ ...icon, x: newX, y: newY }));
                  }}
                  disabled={false}
                >
                  <div
                    style={{
                      position: 'absolute',
                      pointerEvents: 'auto',
                      cursor: 'move',
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      dispatch(deleteIcon({ id: icon.id }));
                    }}
                  >
                    <Icon
                      path={iconPath}
                      size={(icon.size || 1) * Math.min(scaleX, scaleY)}
                      color={icon.color}
                    />
                  </div>
                </Draggable>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
